import {
  Component,
  OnInit,
  ViewChild,
  Input,
  Output,
  EventEmitter,
  ViewEncapsulation,
} from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import * as moment from 'moment';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'iv-custom-date-range-picker',
  templateUrl: './custom-date-range-picker.component.html',
  styleUrls: ['./custom-date-range-picker.component.scss'],
  encapsulation: ViewEncapsulation.None, // Add this line to avoid the style encapsulation or not to use ng-deep
})
export class CustomDateRangePickerComponent implements OnInit {
  @ViewChild('picker') matDateRangeInput;
  @Input() startDate: string = '';
  @Input() endDate: string = '';
  @Input() disableToggleButton: boolean = false;
  @Input() disableDatePicker = true;
  @Input() filterBy: string = '';
  @Output() emitSelectedDates: EventEmitter<any>;

  dateRangePickerForm: UntypedFormGroup;
  displayClearButton: boolean = false;

  openPicker = false;
  minDate = moment(moment.now());
  maxDate = moment(moment.now());
  readonly tabs = ['MTD', 'QTD', 'YTD'];

  constructor(
    private formBuilder: UntypedFormBuilder,
    private activatedRoute: ActivatedRoute
  ) {
    this.emitSelectedDates = new EventEmitter();
  }

  ngOnInit() {
    this.initDateRangePicker();
  }

  initDateRangePicker() {
    if (this.startDate && this.endDate) {
      this.displayClearButton = true;
      this.dateRangePickerForm = this.formBuilder.group({
        startDate: moment(this.startDate),
        endDate: moment(this.endDate),
      });
    } else {
      this.dateRangePickerForm = this.formBuilder.group({
        startDate: undefined,
        endDate: undefined,
      });
    }
  }

  onSelectionChanged(callFrom) {
    const startDate = this.dateRangePickerForm.get('startDate')?.value;
    let endDate = this.dateRangePickerForm.get('endDate')?.value;
    if (callFrom === 'start' && endDate) {
      this.dateRangePickerForm.get('endDate')?.patchValue(undefined);
      endDate = undefined;
    }

    if (startDate && endDate) {
      this.displayClearButton = true;
      this.emitSelectedDates.emit({
        ...this.dateRangePickerForm.value,
        filterBy: this.filterBy,
      });
      this.toggleClass();
    }
  }

  clearDateRange() {
    this.dateRangePickerForm.patchValue(
      { startDate: undefined, endDate: undefined },
      { emitEvent: false }
    );
    this.displayClearButton = false;
    this.emitSelectedDates.emit(this.dateRangePickerForm.value);
  }

  onDatePickerClosed() {
    const startDate = this.dateRangePickerForm.get('startDate')?.value;
    const endDate = this.dateRangePickerForm.get('endDate')?.value;
    if (startDate && !endDate) {
      const lastStartDate = this.startDate ? moment(this.startDate) : undefined;
      const lastEndDate = this.endDate ? moment(this.endDate) : undefined;
      this.dateRangePickerForm.patchValue(
        { startDate: lastStartDate, endDate: lastEndDate },
        { emitEvent: false }
      );
      this.emitSelectedDates.emit(this.dateRangePickerForm.value);
    }
  }

  toggleClass() {
    this.openPicker = !this.openPicker;
    if (this.openPicker) {
      this.initDateRangePicker();
    } else {
      this.filterBy = this.activatedRoute.snapshot.queryParams.filter_by || '';
    }
  }

  onClickedOutside(event) {
    if (event === 'outside' && this.openPicker) {
      this.openPicker = false;
    }
  }

  setDatePickerOptions() {
    switch (this.filterBy) {
      case 'MTD': {
        const startOfMonth = moment(
          moment().clone().startOf('month').format('YYYY-MM-DD')
        );
        const endOfMonth = this.getEndDate('month');
        this.dateRangePickerForm.patchValue({
          startDate: startOfMonth,
          endDate: endOfMonth,
        });
        break;
      }
      case 'QTD': {
        const startOfCurrentQuarter = moment(
          moment().clone().startOf('quarter').format('YYYY-MM-DD')
        );
        const endOfCurrentQuarter = this.getEndDate('quarter');
        this.dateRangePickerForm.patchValue({
          startDate: startOfCurrentQuarter,
          endDate: endOfCurrentQuarter,
        });
        break;
      }
      case 'YTD': {
        this.dateRangePickerForm.patchValue({
          startDate: moment().subtract(1, 'year'), // Today - 12 months
          endDate: this.maxDate,
        });
        break;
      }
    }
  }

  getEndDate(type: 'month' | 'quarter' | 'year') {
    const endDate: any = moment(
      moment().clone().endOf(type).format('YYYY-MM-DD')
    );
    return moment().isSame(this.maxDate, endDate) ? endDate : this.maxDate;
  }

  onFilterChanged(filterType) {
    this.filterBy = filterType;
    this.setDatePickerOptions();
    this.emitSelectedDates.emit({
      ...this.dateRangePickerForm.value,
      filterBy: this.filterBy,
    });
    this.toggleClass();
    this.displayClearButton = true;
  }

  dateFilterFn(d) {
    return moment().isAfter(d, moment(moment.now()) as any);
  }
}
